import type { PropsWithChildren, JSX } from 'react';
import styled from 'styled-components';
import { isNotUndefined, isUndefined, isNotNil } from '~/utilities/type-guards';
import type { Directions, SharedTooltipProps } from '../types';
import { TOOLTIP_WIDTH } from '../styles';
import type { TooltipProps } from '../Tooltip';

const MARGIN = 20;

// Attempt to prevent tooltip from overflowing off-screen. Tooltip width is
// static, so this is easy for `left`, but will require more work to
// support `top` since tooltip height is variable.
const adjustedLeft = (direction: Directions, left: number) => {
  if (['top', 'bottom'].includes(direction)) {
    const maxLeft = window.innerWidth - TOOLTIP_WIDTH - MARGIN;
    return `min(${left}px, ${maxLeft}px)`;
  }
  return `${left}px`;
};

export type ContainerProps = Pick<SharedTooltipProps, 'direction'> & {
  top?: number;
  left?: number;
};

export const AbsoluteContainer = styled.div.attrs<ContainerProps>(({ direction, top, left }) => ({
  style: {
    left: isNotNil(left) ? adjustedLeft(direction, left) : undefined,
    top: isNotNil(top) ? `${top}px` : undefined,
  },
}))<ContainerProps>`
  position: absolute;
  visibility: ${({ top, left }) => (isNotNil(top) && isNotNil(left) ? 'visible' : 'hidden')};
`;

export const RelativeContainer = styled.div<ContainerProps>`
  position: relative;
`;

export type TooltipTargetContainerProps = PropsWithChildren<
  Pick<SharedTooltipProps, 'direction' | 'getTarget'> &
    Pick<TooltipProps, 'getRelativeTarget' | 'hotspotLeft' | 'hotspotTop'>
>;

export const TooltipTargetContainer = ({
  direction,
  getTarget,
  getRelativeTarget,
  children,
  hotspotTop,
  hotspotLeft,
}: TooltipTargetContainerProps): JSX.Element => {
  const isExternalTarget = isNotUndefined(getTarget) && isUndefined(getRelativeTarget);
  const Container = isExternalTarget ? AbsoluteContainer : RelativeContainer;

  const top = hotspotTop;
  const left = hotspotLeft;

  const hasValidPosition = isNotNil(top) && isNotNil(left);

  return (
    <Container
      top={hasValidPosition ? top : undefined}
      left={hasValidPosition ? left : undefined}
      direction={direction}
    >
      {children}
    </Container>
  );
};
